import apiClient from './api';
import type { PackageCategory } from '@/types';

export type PackageType = 'apk' | 'os' | 'fw';

export interface Package {
  id: string;
  package_name: string;
  type: PackageType;
  display_name: string;
  file: string;
  version: string;
  file_name: string;
  file_size_bytes: number;
  md5: string;
  description: string;
  machine_types: string[];
  ats_ids: string[];
  created_at: string;
  updated_at: string;
  /** Set when the package is soft-deleted: file removed, usable in no new rollout. Null/absent = live. */
  deleted_at?: string | null;
}

export interface PackageDefinition {
  display_name: string;
  type: PackageType;
  package_name: string;
  category: PackageCategory;
  sort_order: number;
  /**
   * Whether this identity may be selected in a rollout of its matching type.
   * Replaces the old `rollout_types` list — the rollout-to-package type mapping
   * already provides the type relationship.
   */
  rollout_selectable: boolean;
}

export interface CreatePackageRequest {
  upload_id: string;
  package_name: string;
  type: PackageType;
  version: string;
  description?: string;
  machine_types?: string[];
  ats_ids?: string[];
}

export interface UpdatePackageRequest {
  package_name: string;
  type: PackageType;
  version: string;
  description?: string;
  machine_types?: string[];
  ats_ids?: string[];
}

export interface PackageUploadURLRequest {
  file_name: string;
  md5?: string;
}

export interface PackageUploadURLItem {
  upload_id: string;
  upload_url: string;
  headers: Record<string, string>;
}

export type ChangeFileUploadURLRequest = PackageUploadURLRequest;

export interface CompleteFileChangeRequest extends UpdatePackageRequest {
  upload_id: string;
}

export interface PackageDownloadURL {
  url: string;
  file_name: string;
  file_size_bytes: number;
  expires_at: string;
}

/** Rollout that references a package, returned in the 409 body when delete is blocked. */
export interface RolloutRef {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'archived';
}

/** Body of the 409 Conflict response when deleting a package still used by rollouts. */
export interface PackageInUseConflict {
  detail: string;
  blocking_rollouts: RolloutRef[];
}

export interface PackageRolloutsResponse {
  rollouts: RolloutRef[];
}

const base = '/admin/software-update/packages';
const definitionsBase = '/admin/software-update/package-definitions';

export const softwareUpdatePackageService = {
  list: async (): Promise<Package[]> => {
    return apiClient.get<Package[]>(base);
  },
  listDefinitions: async (): Promise<PackageDefinition[]> => {
    return apiClient.get<PackageDefinition[]>(definitionsBase);
  },
  get: async (id: string): Promise<Package> => {
    return apiClient.get<Package>(`${base}/${id}`);
  },
  listRollouts: async (id: string): Promise<PackageRolloutsResponse> => {
    return apiClient.get<PackageRolloutsResponse>(`${base}/${id}/rollouts`);
  },
  create: async (data: CreatePackageRequest): Promise<Package> => {
    return apiClient.post<Package>(base, data);
  },
  update: async (id: string, data: UpdatePackageRequest): Promise<Package> => {
    return apiClient.put<Package>(`${base}/${id}`, data);
  },
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`${base}/${id}`);
  },
  createUpload: async (data: PackageUploadURLRequest): Promise<PackageUploadURLItem> => {
    return apiClient.post<PackageUploadURLItem>('/admin/software-update/package-uploads', data);
  },
  changeFileUploadURL: async (
    id: string,
    data: ChangeFileUploadURLRequest
  ): Promise<PackageUploadURLItem> => {
    return apiClient.post<PackageUploadURLItem>(`${base}/${id}/file-upload`, data);
  },
  completeFileChange: async (id: string, data: CompleteFileChangeRequest): Promise<Package> => {
    return apiClient.post<Package>(`${base}/${id}/file`, data);
  },
  getDownloadURL: async (id: string): Promise<PackageDownloadURL> => {
    return apiClient.get<PackageDownloadURL>(`${base}/${id}/download-url`);
  },

};

export default softwareUpdatePackageService;
